/**
 * Ctooltip Component
 *
 * Anchored title + body overlay rendered through `Cportal`. Visibility,
 * anchoring, and dismissal are fully controlled by the parent — the
 * component itself only handles positioning, the visual chrome
 * (`Ccontainer` Secondary preset with blur backdrop), enter/exit
 * animation, and optional auto-dismiss / click-outside / Esc bindings
 * (when an `onDismiss` and/or `autoDismissMs` is provided).
 *
 * Width: pass `width` for a fixed-width card (clamped to viewport with
 * padding); omit it for a content-fit card centered on the anchor.
 *
 * Original site of this pattern was the inline overlay inside
 * `Cteamtags.tsx`; this component generalizes it for any "click an
 * inline element, surface a labelled card next to it" flow.
 */
"use client";

import { useRef, type ReactElement } from "react";
import { AnimatePresence } from "framer-motion";
import { Cportal } from "../Cportal";
import { useViewportMetrics } from "@/hooks/dom";
import { CtooltipPositioner } from "./CtooltipPositioner";
import { CtooltipPlacement, type CtooltipProps } from "./Ctooltip.types";
import {
  getTooltipExitYOffset,
  getTooltipIsOpen,
  getTooltipPositionerStyle,
  getTooltipViewportMaxWidth,
} from "./ctooltip-selectors";
import { useTooltipAutoDismiss } from "./useTooltipAutoDismiss";
import { useTooltipDismissal } from "./useTooltipDismissal";
import { useTooltipPosition } from "./useTooltipPosition";

export function Ctooltip({
  dataElScope,
  visible,
  anchorElement,
  title,
  body,
  onDismiss,
  autoDismissMs,
  pauseAutoDismissOnHover = true,
  placement = CtooltipPlacement.Below,
  width,
}: CtooltipProps): ReactElement {
  const { vw } = useViewportMetrics();
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const isOpen = getTooltipIsOpen({ visible, anchorElement });
  const position = useTooltipPosition({
    anchorElement,
    isOpen,
    placement,
    width,
  });
  const hoverHandlers = useTooltipAutoDismiss({
    autoDismissMs,
    isOpen,
    onDismiss,
    pauseAutoDismissOnHover,
  });

  useTooltipDismissal({
    anchorElement,
    isOpen,
    onDismiss,
    tooltipRef,
  });

  if (!isOpen || !position) {
    return (
      <Cportal>
        <AnimatePresence initial={false} />
      </Cportal>
    );
  }

  const viewportMaxWidth = getTooltipViewportMaxWidth({ vw });
  const positionerStyle = getTooltipPositionerStyle({
    hasDismissHandler: Boolean(onDismiss),
    placement,
    position,
    viewportMaxWidth,
  });
  const exitYOffset = getTooltipExitYOffset({ placement });

  return (
    <Cportal>
      <AnimatePresence initial={false}>
        <CtooltipPositioner
          body={body}
          dataElScope={dataElScope}
          exitYOffset={exitYOffset}
          onMouseEnter={hoverHandlers.onMouseEnter}
          onMouseLeave={hoverHandlers.onMouseLeave}
          positionerStyle={positionerStyle}
          title={title}
          tooltipRef={tooltipRef}
        />
      </AnimatePresence>
    </Cportal>
  );
}

export { CtooltipPlacement } from "./Ctooltip.types";
export type { CtooltipProps } from "./Ctooltip.types";
